import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { Search, ChevronDown, ChevronUp, MoreHorizontal, Mail, Phone, Calendar, Users, Inbox, X, Clock } from "lucide-react";

import { db } from "../../firebase";
import VenueSelect from "../../components/VenueSelect";
import Message from "../../components/Message";

function getDate(conversation) {
  const msg = conversation.messages?.[0];
  if (!msg?.date) return null;
  try {
    return msg.date.toDate ? msg.date.toDate() : new Date(msg.date);
  } catch {
    return null;
  }
}

function Requests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedVenue, setSelectedVenue] = useState(null);

  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);

  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    async function fetchRequests() {
      const venueId = selectedVenue || params.venueId;
      if (!venueId) {
        setRequests([]);
        setLoading(false);
        return;
      }

      setError("");
      setLoading(true);

      try {
        const q = query(collection(db, "conversations"), where("venueId", "==", venueId));
        const snapshot = await getDocs(q);
        if (!isMounted) return;

        const data = snapshot.docs.map((doc) => {
          const d = doc.data();
          const date = getDate(d);
          return {
            id: doc.id,
            name: d.prospectName || "Onbekend",
            email: d.prospectEmail || "",
            phone: d.prospectPhone || "",
            guests: d.guests || 0,
            event: d.eventType || "",
            message: d.messages?.[0]?.body || "",
            date,
            dateRaw: date ? date.getTime() : 0,
            type: d.type || "direct",
          };
        });

        setRequests(data);
      } catch (err) {
        console.error("Error fetching requests:", err);
        if (isMounted) setError("Er is een fout opgetreden bij het ophalen van de aanvragen.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchRequests();
    return () => {
      isMounted = false;
    };
  }, [selectedVenue, params.venueId]);

  useEffect(() => {
    if (!selectedRequest) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") setSelectedRequest(null);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [selectedRequest]);

  const handleVenueChange = (value, doc) => {
    if (!doc) {
      setSelectedVenue(null);
      navigate("/requests");
      return;
    }
    setSelectedVenue(value);
    navigate(`/requests/${value}`);
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown size={14} className="text-text-tertiary opacity-0 group-hover:opacity-100" />;
    return sortDir === "asc" ? <ChevronUp size={14} className="text-accent" /> : <ChevronDown size={14} className="text-accent" />;
  };

  const filtered = requests
    .filter((r) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q) || r.event.toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      const modifier = sortDir === "asc" ? 1 : -1;
      if (sortField === "date") return modifier * (a.dateRaw - b.dateRaw);
      if (sortField === "name") return modifier * a.name.localeCompare(b.name);
      if (sortField === "guests") return modifier * (a.guests - b.guests);
      return 0;
    });

  const venueId = selectedVenue || params.venueId;

  return (
    <div>
      <VenueSelect activeVenueId={venueId} onChange={handleVenueChange} showNewButton={false} isStandAlone={true} preloadFirstItem={true} />

      {error && <Message text={error} />}

      {!venueId && !loading && (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-bg-sunken flex items-center justify-center">
            <Inbox size={28} strokeWidth={1.5} className="text-text-tertiary" />
          </div>
          <h3 className="text-lg font-heading font-semibold mb-2">Selecteer een locatie</h3>
          <p className="text-sm text-text-secondary max-w-sm mx-auto">Kies een locatie om de aanvragen te bekijken.</p>
        </div>
      )}

      {venueId && loading && (
        <div className="card overflow-hidden">
          <div className="border-b border-border bg-bg-sunken px-5 py-3">
            <div className="skeleton h-3 w-24 rounded" />
          </div>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-b-0">
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-32 rounded" />
                <div className="skeleton h-3 w-48 rounded" />
              </div>
              <div className="skeleton h-3 w-16 rounded" />
              <div className="skeleton h-5 w-20 rounded-full" />
            </div>
          ))}
        </div>
      )}

      {venueId && !loading && (
        <>
          <div className="mb-6">
            <div className="relative max-w-sm">
              <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                type="text"
                placeholder="Zoek op naam, email of type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-base pl-9"
              />
            </div>
          </div>

          {selectedRequest && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedRequest(null)} />
              <div
                role="dialog"
                aria-modal="true"
                className="relative bg-bg-surface rounded-lg shadow-xl max-w-modal-md w-full animate-slide-up"
              >
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-heading font-bold">{selectedRequest.name}</h3>
                      {selectedRequest.type === "party_request" ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-subtle text-accent">Feestje zoeker</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-info-subtle text-info">Direct</span>
                      )}
                    </div>
                    {selectedRequest.event && <p className="text-sm text-text-secondary mt-0.5">{selectedRequest.event}</p>}
                  </div>
                  <button
                    onClick={() => setSelectedRequest(null)}
                    aria-label="Sluiten"
                    className="p-1.5 rounded-sm text-text-tertiary hover:text-text-primary hover:bg-bg-sunken transition-colors duration-fast"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {selectedRequest.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail size={14} className="text-text-tertiary" />
                        <a href={`mailto:${selectedRequest.email}`} className="text-accent hover:underline">
                          {selectedRequest.email}
                        </a>
                      </div>
                    )}
                    {selectedRequest.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone size={14} className="text-text-tertiary" />
                        <span>{selectedRequest.phone}</span>
                      </div>
                    )}
                    {selectedRequest.date && (
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar size={14} className="text-text-tertiary" />
                        <span>{selectedRequest.date.toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}</span>
                      </div>
                    )}
                    {selectedRequest.guests > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <Users size={14} className="text-text-tertiary" />
                        <span>{selectedRequest.guests} personen</span>
                      </div>
                    )}
                  </div>
                  {selectedRequest.message && (
                    <div className="pt-4 border-t border-border">
                      <p className="text-sm text-text-secondary leading-relaxed">{selectedRequest.message}</p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3 p-6 pt-0">
                  <button
                    onClick={() => setSelectedRequest(null)}
                    className="px-4 py-2 text-sm font-medium text-text-secondary border border-border rounded-sm hover:bg-bg-sunken transition-colors duration-fast"
                  >
                    Sluiten
                  </button>
                  {selectedRequest.email && (
                    <a
                      href={`mailto:${selectedRequest.email}`}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-text-inverse rounded-sm hover:bg-accent-hover transition-colors duration-fast"
                    >
                      <Mail size={14} /> Beantwoorden
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="card p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-bg-sunken flex items-center justify-center">
                <Inbox size={28} strokeWidth={1.5} className="text-text-tertiary" />
              </div>
              <h3 className="text-lg font-heading font-semibold mb-2">Geen aanvragen gevonden</h3>
              <p className="text-sm text-text-secondary max-w-sm mx-auto">
                {searchQuery ? "Probeer een andere zoekterm." : "Zodra bezoekers een aanvraag doen, verschijnen ze hier."}
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-bg-sunken">
                      <th
                        className="text-left text-xs font-medium text-text-secondary uppercase tracking-wider px-5 py-3 cursor-pointer group"
                        onClick={() => toggleSort("name")}
                      >
                        <span className="flex items-center gap-1">
                          Naam <SortIcon field="name" />
                        </span>
                      </th>
                      <th className="text-left text-xs font-medium text-text-secondary uppercase tracking-wider px-5 py-3">Type</th>
                      <th className="text-left text-xs font-medium text-text-secondary uppercase tracking-wider px-5 py-3">Bron</th>
                      <th
                        className="text-right text-xs font-medium text-text-secondary uppercase tracking-wider px-5 py-3 cursor-pointer group"
                        onClick={() => toggleSort("guests")}
                      >
                        <span className="flex items-center justify-end gap-1">
                          Gasten <SortIcon field="guests" />
                        </span>
                      </th>
                      <th
                        className="text-left text-xs font-medium text-text-secondary uppercase tracking-wider px-5 py-3 cursor-pointer group"
                        onClick={() => toggleSort("date")}
                      >
                        <span className="flex items-center gap-1">
                          Datum <SortIcon field="date" />
                        </span>
                      </th>
                      <th className="w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((request) => (
                      <tr
                        key={request.id}
                        className="group border-b border-border last:border-b-0 hover:bg-bg-sunken/50 cursor-pointer transition-colors duration-fast"
                        onClick={() => setSelectedRequest(request)}
                      >
                        <td className="px-5 py-3.5">
                          <div>
                            <span className="text-sm font-medium text-text-primary">{request.name}</span>
                            {request.email && <span className="block text-xs text-text-tertiary">{request.email}</span>}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-text-secondary">{request.event || "—"}</td>
                        <td className="px-5 py-3.5">
                          {request.type === "party_request" ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-subtle text-accent">Feestje zoeker</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-info-subtle text-info">Direct</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-text-primary text-right" data-tabular="true">
                          {request.guests > 0 ? request.guests : "—"}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-text-secondary">
                          {request.date ? (
                            <span className="flex items-center gap-1">
                              <Clock size={12} className="text-text-tertiary" />
                              {request.date.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-3 py-3.5">
                          <button
                            className="p-1.5 rounded-sm text-text-tertiary hover:text-text-primary hover:bg-bg-sunken transition-colors duration-fast opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRequest(request);
                            }}
                          >
                            <MoreHorizontal size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-bg-sunken">
                <span className="text-xs text-text-tertiary">
                  {filtered.length} van {requests.length} aanvragen
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Requests;
